import {
  Box,
  ContentBlock,
  Dialog,
  Heading,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import React from 'react';

import {
  formatEventTileDate,
  renderDescriptionParagraphs,
  splitEventsForCalendar,
} from '#shared/eventUtils';
import type { PublishedEvent } from '#shared/types';
import { fetchEvents } from '#src/api/events';
import { PageWrapper } from '#src/components/PageWrapper/PageWrapper';

import { CalendarEventCard } from './CalendarEventCard';

const EventSection = ({
  heading,
  events,
  emptyMessage,
  onSelect,
}: {
  heading: string;
  events: PublishedEvent[];
  emptyMessage: string;
  onSelect: (event: PublishedEvent) => void;
}) => (
  <Stack space="medium">
    <Heading level="2">{heading}</Heading>
    {events.length === 0 ? (
      <Text>{emptyMessage}</Text>
    ) : (
      <Tiles space="medium" columns={{ mobile: 1, tablet: 3 }}>
        {events.map((event) => (
          <CalendarEventCard
            key={event.id}
            title={event.title}
            date={formatEventTileDate(event)}
            image={event.imageUrl}
            onClick={() => onSelect(event)}
          />
        ))}
      </Tiles>
    )}
  </Stack>
);

export const Calendar = () => {
  const [events, setEvents] = React.useState<PublishedEvent[]>([]);
  const [activeEvent, setActiveEvent] = React.useState<PublishedEvent | null>(
    null,
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchEvents()
      .then((snapshot) => setEvents(snapshot.events))
      .catch(() => setError('Unable to load events'))
      .finally(() => setLoading(false));
  }, []);

  const { upcoming, past } = splitEventsForCalendar(events);

  return (
    <PageWrapper>
      <ContentBlock>
        <Stack space="large">
          <Heading level="1">Calendar</Heading>
          <Text>
            Upcoming PFA events and recent activities at Amsleigh Park Primary
            School.
          </Text>

          {loading ? <Text>Loading events…</Text> : null}
          {error ? <Text tone="critical">{error}</Text> : null}
          {!loading && !error && events.length === 0 ? (
            <Text>No events scheduled yet.</Text>
          ) : null}
          {!loading && !error && events.length > 0 ? (
            <Stack space="xlarge">
              <EventSection
                heading="Upcoming events"
                events={upcoming}
                emptyMessage="No upcoming events."
                onSelect={setActiveEvent}
              />
              <EventSection
                heading="Past events"
                events={past}
                emptyMessage="No past events."
                onSelect={setActiveEvent}
              />
            </Stack>
          ) : null}
        </Stack>
      </ContentBlock>

      <Dialog
        id="calendar-event-details"
        title={activeEvent?.title ?? ''}
        description={
          activeEvent ? (
            <Text tone="secondary">{formatEventTileDate(activeEvent)}</Text>
          ) : undefined
        }
        illustration={
          activeEvent?.imageUrl ? (
            <img
              src={activeEvent.imageUrl}
              alt=""
              style={{ maxHeight: '120px' }}
            />
          ) : undefined
        }
        open={activeEvent !== null}
        onClose={() => setActiveEvent(null)}
        width="small"
      >
        {activeEvent ? (
          <Stack space="medium">
            {renderDescriptionParagraphs(activeEvent.description).map(
              (paragraph) => (
                <Text key={paragraph.slice(0, 40)}>{paragraph}</Text>
              ),
            )}
          </Stack>
        ) : null}
      </Dialog>
    </PageWrapper>
  );
};
